import React, { useMemo } from "react";
import {
  Box,
  Checkbox,
  Divider,
  Grid2,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Account } from "@rentablo/api/apiv1";
import InfoIcon from "@icons/myicons/info.svg?react";
import { TypographyWithInfoIcon } from "@rentablo/components";
import { useAccountsSortAndFilter } from "@components/Accounts/useAccountsSortAndFilter";
import { getAccountIcon } from "@icons/utils";
import {
  AccountWithSyncRequired,
  useAccountsWithSyncRequired,
} from "../../../hooks/useAccountsWithSyncRequired";

type Props = {
  accounts?: Account[];
  checkedPortfolios: number[];
  onSelect: (ids: number[]) => void;
  /** Still selected along with their depot, just not listed */
  hideReferenceAccounts?: boolean;
  /** Of the scrolling list, below the header */
  maxHeight?: number;
};

/** A depot's reference account, which is selected together with its depot */
export const isReferenceAccount = (account: Account, accounts: Account[]) =>
  Boolean(account.depotIds?.length) &&
  accounts.some((acc) => account.depotIds?.includes(acc.id));

export const AccountsTable = ({
  accounts = [],
  checkedPortfolios = [],
  onSelect,
  hideReferenceAccounts = false,
  maxHeight,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isLessSm = useMediaQuery(theme.breakpoints.down("sm"));
  const accountsWithSyncFlag = useAccountsWithSyncRequired(accounts);
  const { sortedAccounts } = useAccountsSortAndFilter(accountsWithSyncFlag, {
    defaultSort: { field: "name", direction: "ASC" },
  });

  const areAllChecked = Boolean(
    checkedPortfolios.length && checkedPortfolios.length === accounts.length
  );

  const shouldHideCheckbox = (account: Account) =>
    isReferenceAccount(account, accounts);

  const onCheckAll = () => {
    if (areAllChecked) {
      onSelect([]);
    } else {
      onSelect(accounts.map((item) => item.id) || []);
    }
  };

  const onCheck = (account: Account) => {
    if (shouldHideCheckbox(account)) return;

    const id = account.id;
    const referenceIds =
      accounts
        .filter((account) => account.depotIds?.includes(id))
        ?.map((x) => x.id) || [];
    const allIds = [...referenceIds, id];

    if (checkedPortfolios.includes(id)) {
      onSelect(
        checkedPortfolios.filter(
          (checkPortfolioId) => !allIds.includes(checkPortfolioId)
        )
      );
    } else {
      onSelect([...checkedPortfolios, ...allIds]);
    }
  };

  const sortedWithReferenceAccounts = useMemo(() => {
    const byId = new Map(
      sortedAccounts.map((account) => [account.id, account])
    );
    const referencedIds = new Set<number>();
    sortedAccounts.forEach((account) => {
      if (account.contraAccountId != null) {
        referencedIds.add(account.contraAccountId);
      }
      (account.additionalContraAccountIds ?? []).forEach((id) =>
        referencedIds.add(id)
      );
    });

    const result: (AccountWithSyncRequired & {
      isReferenceAccount?: boolean;
    })[] = [];

    sortedAccounts.forEach((account) => {
      // reference accounts are rendered grouped under their depot, not standalone
      if (referencedIds.has(account.id)) return;
      result.push(account);
      if (hideReferenceAccounts) return;

      // append the depot's reference accounts, primary first, then additional
      const orderedReferenceIds = [
        account.contraAccountId,
        ...(account.additionalContraAccountIds ?? []),
      ].filter((id): id is number => id != null);

      const added = new Set<number>();
      orderedReferenceIds.forEach((id) => {
        if (added.has(id)) return;
        added.add(id);
        const refAccount = byId.get(id);
        if (refAccount) {
          result.push({ ...refAccount, isReferenceAccount: true });
        }
      });
    });

    return result;
  }, [sortedAccounts, hideReferenceAccounts]);

  return (
    <>
      <Grid2 container>
        <Grid2 py={1} size={6}>
          <Box
            display="flex"
            gap={3}
            alignItems="center"
            pl={3}
            sx={{ position: "sticky" }}
          >
            <Checkbox
              checked={areAllChecked}
              onClick={onCheckAll}
              data-testid="check-all"
            />
            <Box display="flex" gap={1} alignItems="center">
              <TypographyWithInfoIcon
                tooltipText={t("portfolioShare.modal.table.selectAll")}
                icon={<InfoIcon color={theme.palette.tertiary.main} />}
                variant="caption2"
              >
                {t("portfolioShare.modal.table.name")}
              </TypographyWithInfoIcon>
            </Box>
          </Box>
        </Grid2>
        <Grid2 pr={3} display="flex" alignItems="center" size={6}>
          <Typography variant="caption2">
            {t("portfolioShare.modal.table.bank")}
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Divider />
        </Grid2>
      </Grid2>
      <Grid2
        container
        columnSpacing={3}
        sx={{
          overflowY: "auto",
          maxHeight: maxHeight ?? (isLessSm ? 350 : 500),
        }}
      >
        {sortedWithReferenceAccounts.map((item, index) => {
          const isSelectable = !shouldHideCheckbox(item);
          return (
            <React.Fragment key={`${item.id}-${index}`}>
              <Grid2
                pl={3}
                py={1}
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => onCheck(item)}
                size={6}
              >
                <Box display="flex" alignItems="center" gap={3} pl={3}>
                  {isSelectable && (
                    <Checkbox
                      checked={checkedPortfolios.includes(item.id)}
                      data-testid="checkbox-depot-row"
                    />
                  )}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      overflow: "hidden",
                      marginLeft: !isSelectable ? "50px" : "0",
                      padding: !isSelectable ? "9px 0" : "0",
                    }}
                  >
                    {getAccountIcon(theme, 24, item, isSelectable)}
                    <Typography variant="body4" noWrap>
                      {item.name}
                    </Typography>
                  </Box>
                </Box>
              </Grid2>
              <Grid2
                pr={3}
                onClick={() => onCheck(item)}
                display="flex"
                alignItems="center"
                sx={{
                  cursor: "pointer",
                }}
                size={6}
              >
                <Box
                  sx={{
                    overflow: "hidden",
                  }}
                >
                  <Typography variant="body3" fontWeight={400} noWrap>
                    {item.bankName}
                  </Typography>
                </Box>
              </Grid2>
              {index !== (accounts.length || 0 - 1) && (
                <Grid2 size={12}>
                  <Divider />
                </Grid2>
              )}
            </React.Fragment>
          );
        })}
      </Grid2>
    </>
  );
};
